import {ContentHotel, Hotel} from '@findhotel/sapi'
import {generateHotelDescription} from 'components/Accommodation/utils/generateHotelDescription'
import {imageProvider} from 'utils/imageProvider'
import {ApSeoBreadcrumb} from 'utils/server/apSeo/getApSeoBreadcrumb'

import {htmlToPlainText} from '@vio/core/src/_web/utils/dom'
import {getBrandLogo} from '@vio/core/src/offer/business/provider'

import type {Brand} from '@vio/core/src/brand/types'
import type {SeoBreadcrumb} from 'utils/server/searchSeo/getSeoBreadcrumb'

type SchemaFromHotelKind =
  | 'PostalAddress'
  | 'Rating'
  | 'AggregateRating'
  | 'Image'
  | 'Description'

// Google wants at least 1200px wide for hotel rich results. Height is left
// unbounded so portrait images keep that width instead of being capped by it.
export const SEO_IMAGE_SIZE: [number, number] = [1200, 0]

export const getOrganizationSchema = ({brand}: {brand: Brand}) => {
  const baseUrl = brand.siteAddresses.address
  const brandLogoUrl = getBrandLogo({
    providerCode: brand.code
  })

  return {
    '@type': 'Organization',
    '@id': `${baseUrl}/#organization`,
    name: brand.name,
    url: `${baseUrl}/`,
    logo: {
      '@type': 'ImageObject',
      url: brandLogoUrl
    }
  }
}

export const getWebsiteSchema = ({brand}: {brand: Brand}) => {
  const baseUrl = brand.siteAddresses.address

  return {
    '@type': 'WebSite',
    '@id': `${baseUrl}/#website`,
    url: `${baseUrl}/`,
    name: brand.name,
    publisher: {
      '@id': `${baseUrl}/#organization`
    }
  }
}

export const getSchemaFromHotel = (
  kind: SchemaFromHotelKind,
  hotel: Partial<ContentHotel> | Partial<Hotel>,
  {
    maxNumberOfImages = 1,
    preferHeadline = false
  }: {maxNumberOfImages?: number; preferHeadline?: boolean} = {}
): Record<string, unknown> => {
  if (kind === 'PostalAddress') {
    const {city, country, adminDivision} = hotel.navPathInfo ?? {}
    // Schema.org prefers the two-letter ISO code over the country name
    const addressCountry = hotel.country || country?.name

    if (!hotel.displayAddress) return {}

    return {
      address: {
        '@type': 'PostalAddress',
        // displayAddress is "street, neighbourhood, city, region, country";
        // schema.org wants the street line only.
        streetAddress: hotel.displayAddress.split(',')[0].trim(),
        ...(city?.name && {addressLocality: city.name}),
        ...(adminDivision?.name && {addressRegion: adminDivision.name}),
        ...(addressCountry && {addressCountry})
      }
    }
  }

  if (kind === 'Image') {
    const images = (hotel.imageURIs ?? []).slice(0, maxNumberOfImages)
    if (!images.length) return {}

    return {
      image: images.map(imageUrl => imageProvider(imageUrl, SEO_IMAGE_SIZE))
    }
  }

  if (kind === 'Rating') {
    if (!hotel.starRating) return {}

    return {
      starRating: {
        '@type': 'Rating',
        ratingValue: hotel.starRating
      }
    }
  }

  if (kind === 'AggregateRating') {
    const hasGuestRatings = Boolean(
      hotel.guestRating?.overall && hotel.reviewCount
    )
    if (!hasGuestRatings) return {}

    return {
      aggregateRating: {
        '@type': 'AggregateRating',
        bestRating: 10,
        ratingValue: hotel.guestRating.overall,
        reviewCount: hotel.reviewCount
      }
    }
  }

  if (kind === 'Description') {
    const {descriptionDisplayed} = generateHotelDescription(
      hotel.descriptions,
      {preferHeadline}
    )
    if (!descriptionDisplayed) return {}

    return {
      description: htmlToPlainText(descriptionDisplayed)
    }
  }

  return {}
}

export const getBreadcrumbSchema = ({
  baseUrl,
  breadcrumb,
  seoPageUrl
}: {
  baseUrl: string
  breadcrumb: SeoBreadcrumb[] | ApSeoBreadcrumb[]
  seoPageUrl: string
}) => {
  const breadcrumbList = breadcrumb.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: `${baseUrl}${item.path}`
  }))

  return {
    '@type': 'BreadcrumbList',
    '@id': `${seoPageUrl}#breadcrumb`,
    itemListElement: breadcrumbList
  }
}
